import { Divider, Menu, MenuItem, Typography } from "@mui/material";
import { memo, MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import { palette } from "../../../styles/theme";
import { useSelector } from "../../hooks/useSelector";
import { AdminIcon, Icon } from "../Icon";

export interface MenuOption {
  labelKey?: string;
  label?: string;
  disabled?: boolean;
  isDivider?: boolean;
  onClick?: (e: MouseEvent<HTMLElement>) => void;
  isChecked?: () => boolean;
  hideIf?: (() => boolean) | boolean;
  adminOnly?: boolean;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  target: Element | null | undefined;
  options: MenuOption[];
}

const MenuControlled = ({
  options,
  isOpen,
  onClose,
  target,
  ...rest
}: Props) => {
  const { t } = useTranslation();
  const userInfo = useSelector((state) => state.userInfo);
  const isAdmin = userInfo.isAdmin || userInfo.isDesigner;

  const onClick =
    (callback?: (e: MouseEvent<HTMLElement>) => void) =>
    (e: MouseEvent<HTMLElement>) => {
      callback?.(e);
      onClose();
    };

  return (
    <Menu
      open={isOpen}
      id="menu-controlled"
      anchorEl={target}
      onClose={onClose}
      {...rest}
      slotProps={{
        root: {
          onClick: (e) => {
            e.stopPropagation();
          },
        },
      }}
      sx={{
        "& .MuiPaper-root": {
          width: 200,
          minWidth: "fit-content",
        },
      }}
    >
      {options.map((option, index) => {
        if (
          (typeof option.hideIf === "function" && option.hideIf?.()) ||
          option.hideIf === true ||
          (option.adminOnly && !isAdmin)
        ) {
          return null;
        }
        if (option.isDivider) {
          return <Divider key={`${option.labelKey}-${index}`} />;
        }

        return (
          <MenuItem
            onClick={onClick(option.onClick)}
            key={`${option.labelKey}-${index}`}
            disabled={option.disabled}
            data-test={option.labelKey}
          >
            <Typography
              variant="actionSmallIcon"
              display={"flex"}
              alignItems={"center"}
              gap={"4px"}
            >
              {option.labelKey && t(option.labelKey)}
              {option.label}
              {option.adminOnly && <AdminIcon />}
            </Typography>
            {option.isChecked?.() && (
              <Icon icon="done" color={palette.grey.nearBlack} size={18} />
            )}
          </MenuItem>
        );
      })}
    </Menu>
  );
};

export default memo(MenuControlled);
